import { useCallback, useMemo } from "react"
import { ButtonProps } from "@mezo-org/mezo-clay"

import { usePosthogButtonClickedEvent } from "#/shared/hooks"
import { useMinBridgeAmount } from "#/shared/hooks/bridge"
import { BridgeableTokens, PosthogAnalyticsId } from "#/shared/types"

import { BridgeAssetButton } from "../Button"

type OverviewBridgeDepositButtonProps = ButtonProps & {
  depositId: number
  depositAmount: bigint
  token: BridgeableTokens
}
type OverviewBridgeDepositButtonBaseProps = Omit<
  OverviewBridgeDepositButtonProps,
  "token" | "depositAmount"
>

function OverviewBridgeDepositButtonBase({
  depositId,
  ...rest
}: OverviewBridgeDepositButtonBaseProps) {
  const { emitButtonClickedEvent } = usePosthogButtonClickedEvent()

  const bridgeButtonHandler = useCallback(() => {
    emitButtonClickedEvent(PosthogAnalyticsId.ASSETS_BRIDGE)
  }, [emitButtonClickedEvent])

  return (
    <BridgeAssetButton
      shape="pill"
      kind="secondary"
      size="small"
      onClick={bridgeButtonHandler}
      depositId={depositId}
      {...rest}
    >
      Bridge
    </BridgeAssetButton>
  )
}

export default function OverviewBridgeDepositButton({
  depositId,
  depositAmount,
  token,
  ...props
}: OverviewBridgeDepositButtonProps) {
  const { data: minTokenAmount, isFetching: isWaitingForMinBridgeAmount } =
    // The `BTC` deposit is represented as `tBTC` in the Portal contract so the
    // `MezoBridge.minTBTCAmount` requirement must be met.
    useMinBridgeAmount(token === "BTC" ? "tBTC" : (token as BridgeableTokens))

  const isDepositAmountEnough = useMemo(() => {
    if (isWaitingForMinBridgeAmount) return false

    return depositAmount >= minTokenAmount
  }, [depositAmount, minTokenAmount, isWaitingForMinBridgeAmount])

  if (!isDepositAmountEnough) return null

  return <OverviewBridgeDepositButtonBase depositId={depositId} {...props} />
}
